import React, { useEffect, useState, useMemo } from 'react';
import { 
    Grid, Box, Card, CardContent, Typography, Paper, Tooltip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert,IconButton
} from "@mui/material";
import PaidIcon from '@mui/icons-material/Paid';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import GroupsIcon from '@mui/icons-material/Groups';
import Axios from 'axios';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

export const ResumenCobranzas = ({ id_evento }) => {
    const [eventoActivo, setEventoActivo] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (id_evento) {
            obtenerEstadoActual();
        }
    }, [id_evento]);

    const obtenerEstadoActual = async () => {
        setCargando(true);
        setError(null);
        try {
            const { data } = await Axios.get(`api/tablasgenerales/eventos/cobranzas/${id_evento}`);
            // Nos aseguramos de guardar siempre un array válido
            setEventoActivo(Array.isArray(data) ? data : [data]);
        } catch (err) {
            console.error("Error al obtener resumen de cobranzas:", err);
            setError("No se pudo recuperar el resumen financiero del evento.");
        } finally {
            setCargando(false);
        }
    };


const totalesGlobales = useMemo(() => {
    if (!eventoActivo || eventoActivo.length === 0) {
        return { dineroTotal: 0, inscriptosTotal: 0, transaccionesTotal: 0 };
    }

    return eventoActivo.reduce((acc, curr) => {
        return {
            dineroTotal: acc.dineroTotal + (Number(curr.importe_total) || 0),
            inscriptosTotal: acc.inscriptosTotal + (Number(curr.cantidad_total_inscriptos) || 0),
            transaccionesTotal: acc.transaccionesTotal + (Number(curr.cantidad_cobranzas) || 0)
        };
    }, { dineroTotal: 0, inscriptosTotal: 0, transaccionesTotal: 0 });
}, [eventoActivo]);    

    // 📝 Formateamos la información de manera ejecutiva para el portapapeles
const textoResumenFinanciero = useMemo(() => {
    if (!eventoActivo || eventoActivo.length === 0) return "";

    let texto = `📊 *RESUMEN DE COBRANZAS*\n`;
    texto += `----------------------------------\n`;
    texto += `💰 Recaudación Total: $ ${totalesGlobales.dineroTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}\n`;
    texto += `👥 Inscriptos Totales: ${totalesGlobales.inscriptosTotal} personas\n\n`;
    texto += `📋 *Desglose por Canal:*\n`;

    eventoActivo.forEach(row => {
        const nombreCanal = row.id_cobrador === null ? 'MercadoPago' : row.cobrador_nombre;
        texto += `• ${nombreCanal}: $ ${Number(row.importe_total).toLocaleString('es-AR', { minimumFractionDigits: 2 })} (${row.cantidad_total_inscriptos} inscr.)\n`;
    });

    return texto;
}, [eventoActivo, totalesGlobales]);


    if (cargando) {
        return (
            <Box sx={{ width: '100%', p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Procesando flujos de caja y conciliando datos...
                </Typography>
            </Box>
        );
    }

    if (error) return <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>;

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            <Grid container spacing={2}>
                {/* Total de Caja General */}
                <Grid item xs={12} sm={6}>
                    <Card sx={{ borderRadius: '10px', border: '1px solid #eef2f6', boxShadow: '0px 2px 8px rgba(0,0,0,0.02)' }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent:'space-between', p: 2, '&:last-child': { pb: 2 } }}>
                            <Box>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary' }}>
                                    Recaudación Total Evento
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5, color: '#2e7d32' }}>
                                    $ {totalesGlobales.dineroTotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                            </Box>
                          
                        </CardContent>
                    </Card>
                </Grid>

                {/* Total de Inscriptos */}
                <Grid item xs={12}  sm={6}>
                    <Card sx={{ borderRadius: '10px', border: '1px solid #eef2f6', boxShadow: '0px 2px 8px rgba(0,0,0,0.02)' }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent:'space-between', p: 2, '&:last-child': { pb: 2 } }}>
                            <Box>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary' }}>
                                    Total de inscriptos
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5, color: '#1976d2' }}>
                                    {totalesGlobales.inscriptosTotal} <Box component="span" sx={{ fontSize: '0.95rem', fontWeight: 500, color: 'text.secondary' }}>personas</Box>
                                </Typography>
                            </Box>
                          
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2d3748' }}>
                    Detalle de Rendición por Operador
                </Typography>
                {/* Insertamos acá el componente pasándole el string formateado */}
                <BotonCopiarPortapapeles textoACopiar={textoResumenFinanciero} />
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eef2f6', borderRadius: '10px', overflow: 'hidden' }}>
                <Table size="small" aria-label="resumen de cobranzas">
                    <TableHead sx={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #edf2f7' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#4a5568', fontWeight: 700, py: 1.5 }}>Canal / Cobrador</TableCell>
                            <TableCell sx={{ color: '#4a5568', fontWeight: 700, py: 1.5 }} align="center">ID Ref.</TableCell>
                            <TableCell sx={{ color: '#4a5568', fontWeight: 700, py: 1.5 }} align="right">Inscripciones</TableCell>
                            <TableCell sx={{ color: '#4a5568', fontWeight: 700, py: 1.5 }} align="right">Inscriptos</TableCell>
                            <TableCell sx={{ color: '#4a5568', fontWeight: 700, py: 1.5 }} align="right">Recaudación</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {eventoActivo.map((row, index) => {
                            const esMercadoPago = row.id_cobrador === null;
                            const esBecado = row.id_cobrador === 0;

                            return (
                                <TableRow 
                                    key={row.id_cobrador ?? `mp-${index}`}
                                    sx={{ 
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': { backgroundColor: '#f8faff' },
                                        transition: 'background-color 0.2s ease'
                                    }}
                                >
                                <TableCell sx={{ fontWeight: 600, py: 1.5, color: '#2d3748', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {renderIconoCanal(row.id_cobrador)}
                                    <Box component="span" sx={{ textTransform: row.id_cobrador > 0 ? 'uppercase' : 'none', fontSize: '0.9rem' }}>
                                        {row.cobrador_nombre}
                                    </Box>
                                </TableCell>

                                    {/* ID Referencia */}
                                    <TableCell align="center" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                                        {row.id_cobrador ?? 'WEB'}
                                    </TableCell>

                                    {/* Cantidad Cobranzas */}
                                    <TableCell align="right" sx={{ color: '#4a5568', fontWeight: 500 }}>
                                        {row.cantidad_cobranzas}
                                    </TableCell>

                                    {/* Total Inscriptos */}
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#4a5568' }}>
                                        {row.cantidad_total_inscriptos}
                                    </TableCell>

                                    {/* Importe Total por Fila */}
                                    <TableCell 
                                        align="right" 
                                        sx={{ 
                                            fontWeight: 700, 
                                            color: row.importe_total > 0 ? '#2e7d32' : 'text.disabled' 
                                        }}
                                    >
                                        $ {Number(row.importe_total).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const renderIconoCanal = (id_cobrador) => {
    if (id_cobrador === null) {
        // 💳 MercadoPago: Celeste Fintech
        return <CreditCardIcon sx={{ color: '#00aae4', fontSize: 20 }} />;
    }
    if (id_cobrador === 0) {
        // 🎓 Becados: Violeta Institucional
        return <WorkspacePremiumIcon sx={{ color: '#7b1fa2', fontSize: 20 }} />;
    }
    // 💼 Cobradores Humanos: Gris Slate Ejecutivo
    return <AssignmentIndIcon sx={{ color: '#4a5568', fontSize: 20 }} />;
};

const BotonCopiarPortapapeles = ({ textoACopiar }) => {
    const [copiado, setCopiado] = useState(false);

    const handleCopiar = async () => {
        try {
            await navigator.clipboard.writeText(textoACopiar);
            setCopiado(true);
            // Volver al ícono original después de 2 segundos
            setTimeout(() => setCopiado(false), 2000);
        } catch (err) {
            console.error("Error al copiar al portapapeles:", err);
        }
    };

    return (
        <Tooltip title={copiado ? "¡Copiado!" : "Copiar resumen al portapapeles"} arrow placement="top">
            <span className='text-xs font-semibold'>Copiar</span>
            <IconButton 
                onClick={handleCopiar} 
                title="Copiar al portapapeles"
                color={copiado ? "success" : "primary"}
                sx={{ 
                    transition: 'all 0.2s ease',
                    backgroundColor: copiado ? '#e8f5e9' : 'transparent',
                    '&:hover': { backgroundColor: copiado ? '#e8f5e9' : '#f5f5f5' }
                }}
            >
                {copiado ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
        </Tooltip>
    );
};